import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MUIButton,
  Stack,
  Typography,
  CircularProgress,
  Box,
  Collapse,
  Divider,
  Chip,
  Skeleton,
  Alert,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import useAgentInfo from './useAgentInfo';

type Row = {
  userId: string;
  [k: string]: any;
};

type LiteInfo = {
  userId?: string;
  isInfiniteAgent?: boolean;
  users?: Array<{
    userId: string;
    userLevel?: string;
    walletAddresses?: string[];
    directReferrals?: string[];
  }>;
};

export default function AgentInfoDetails({
  row,
  open,
  onClose,
}: {
  row: Row;
  open?: boolean;
  onClose: () => void;
}) {
  const t = useTranslate();
  const notify = useNotify();
  const { findAgentInfo } = useAgentInfo();

  const [loading, setLoading] = useState<boolean>(true);
  const [record, setRecord] = useState<any>(null);

  const [expanded, setExpanded] = useState<null | 'l1' | 'l2' | 'l3'>(null);
  const [loadingKey, setLoadingKey] = useState<null | 'l1' | 'l2' | 'l3'>(null);
  const [errorKey, setErrorKey] = useState<null | 'l1' | 'l2' | 'l3'>(null);
  const [cache, setCache] = useState<Record<string, LiteInfo>>({});

  const cfg = record?.infiniteAgentConfig;
  const user0 = Array.isArray(record?.users) ? record.users[0] : undefined;

  const snapshot = user0?.referralSnapshot;

  const uplineRows = useMemo(
    () =>
      snapshot
        ? (
            [
              { key: 'l1' as const, label: 'L1 Upline', id: snapshot.l1UplineId },
              { key: 'l2' as const, label: 'L2 Upline', id: snapshot.l2UplineId },
              { key: 'l3' as const, label: 'L3 Upline', id: snapshot.l3UplineId },
            ] as const
          ).filter((r) => !!r.id)
        : [],
    [snapshot]
  );

  useEffect(() => {
    if (!open || !row?.userId) return;
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await findAgentInfo(row?.userId);
        if (!alive) return;
        setRecord(data || null);
      } catch (e: any) {
        notify(t(e?.body?.message) || t(e?.message) || t('ra.notification.http_error'), {
          type: 'warning',
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, row?.userId, findAgentInfo, notify, t]);

  if (open === false) return null;

  const KV = ({ label, value }: { label: any; value: any }) => (
    <Stack direction="row" spacing={2}>
      <Typography sx={{ flexBasis: '20%', color: 'text.secondary' }} variant="body2">
        {label}
      </Typography>
      <Typography sx={{ flexGrow: 1, wordBreak: 'break-all' }} variant="body2">
        {value ?? '-'}
      </Typography>
    </Stack>
  );

  const handleToggle = async (key: 'l1' | 'l2' | 'l3', id: string) => {
    setExpanded((prev) => (prev === key ? null : key));
    setErrorKey(null);
    if (!id) return;
    if (cache[id]) return;

    try {
      setLoadingKey(key);
      const data = await findAgentInfo(id);
      setCache((prev) => ({ ...prev, [id]: data }));
    } catch {
      setErrorKey(key);
    } finally {
      setLoadingKey(null);
    }
  };

  const UplineMiniCard = ({ data }: { data: LiteInfo | null }) => {
    if (!data) return null;
    const first = data?.users?.[0];

    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: 'background.default',
        }}
      >
        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>User ID:</strong> {data.userId}
          </Typography>

          {typeof data.isInfiniteAgent === 'boolean' && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                <strong>Infinite Agent:</strong>
              </Typography>
              <Chip
                size="small"
                label={data.isInfiniteAgent ? 'True' : 'False'}
                color={data.isInfiniteAgent ? 'success' : 'default'}
                variant="outlined"
              />
            </Stack>
          )}

          {first?.userLevel && (
            <Typography variant="body2">
              <strong>User Level:</strong> {first.userLevel}
            </Typography>
          )}

          {!!first?.walletAddresses?.length && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              <strong>Wallets:</strong> {first.walletAddresses.join(',\n')}
            </Typography>
          )}
          {!!first?.directReferrals?.length && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              <strong>DirectReferrals:</strong> {first.directReferrals.join(',\n')}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('module.agentInfo.details') || 'Details'}</DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : !record ? (
          <Typography color="text.secondary">{t('module.agentInfo.empty')}</Typography>
        ) : (
          <Stack spacing={2}>
            <KV label={t('module.agentInfo.userId')} value={record?.userId || record?.id} />
            <KV
              label={t('module.agentInfo.isInfiniteAgent')}
              value={record?.isInfiniteAgent ? 'Yes' : 'No'}
            />

            {cfg && (
              <>
                <KV
                  label={t('module.agentInfo.commissionRateN') || 'Commission Rate N'}
                  value={cfg?.commissionRateN}
                />
                <KV label={t('module.agentInfo.status') || 'Status'} value={cfg?.status} />
                <KV
                  label={t('module.agentInfo.createdAt') || 'Created At'}
                  value={cfg?.createdAt ? dayjs(cfg.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                />
                <KV
                  label={t('module.agentInfo.updatedAt') || 'Updated At'}
                  value={cfg?.updatedAt ? dayjs(cfg.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                />
              </>
            )}

            {!!user0 && (
              <>
                <KV label={t('module.agentInfo.userLevel')} value={user0?.userLevel} />
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" sx={{ flexBasis: '20%', color: 'text.secondary' }}>
                    {t('module.agentInfo.walletAddresses')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ flexGrow: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-all', mb: 1 }}
                  >
                    {(user0?.walletAddresses || []).join(',\n')}
                  </Typography>
                </Stack>
              </>
            )}

            {cfg?.user && (
              <>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('module.agentInfo.user')}
                </Typography>
                <Stack spacing={1}>
                  <KV label="Email" value={cfg.user.email} />
                  <KV label="Level ID" value={cfg.user.agentLevelId} />
                  <KV label="Invitation Code" value={cfg.user.invitationCode} />
                </Stack>
              </>
            )}

            {!!uplineRows.length && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                  Upline Chain
                </Typography>

                <Stack divider={<Divider flexItem />} spacing={1}>
                  {uplineRows.map(({ key, label, id }) => {
                    const isOpen = expanded === key;
                    const loadingUpline = loadingKey === key;
                    const err = errorKey === key;
                    const data = id ? cache[id] : null;

                    return (
                      <Box key={key}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          justifyContent="space-between"
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {label}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ wordBreak: 'break-all', whiteSpace: 'normal' }}
                              title={id}
                            >
                              {id}
                            </Typography>
                          </Box>

                          <IconButton
                            size="small"
                            onClick={() => id && handleToggle(key, String(id))}
                          >
                            {isOpen ? (
                              <ExpandLessIcon color="success" />
                            ) : (
                              <ExpandMoreIcon color="success" />
                            )}
                          </IconButton>
                        </Stack>

                        <Collapse in={isOpen} unmountOnExit timeout="auto">
                          <Box sx={{ mt: 1.25 }}>
                            {loadingUpline && <Skeleton variant="rounded" height={88} />}
                            {err && (
                              <Alert severity="error" variant="outlined">
                                Failed to load upline detail.
                              </Alert>
                            )}
                            {!loadingUpline && !err && <UplineMiniCard data={data ?? null} />}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <MUIButton variant="outlined" onClick={onClose}>
          {t('actions.cancel')}
        </MUIButton>
      </DialogActions>
    </Dialog>
  );
}
